import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";

const DIAS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

export function ModalRestricoes({
  profissionalId,
  profissionalNome,
  open,
  onOpenChange,
}: {
  profissionalId: number;
  profissionalNome: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const restrQ = trpc.restricoes.listarPorProfissional.useQuery({
    profissionalId,
    apenasAtivas: false,
  });
  const criar = trpc.restricoes.criar.useMutation({ onSuccess: () => restrQ.refetch() });
  const toggle = trpc.restricoes.toggleAtiva.useMutation({ onSuccess: () => restrQ.refetch() });
  const excluir = trpc.restricoes.excluir.useMutation({ onSuccess: () => restrQ.refetch() });

  const [tipo, setTipo] = useState<"bloqueio_data" | "bloqueio_dia_semana" | "preferencia">("bloqueio_data");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [diaSemana, setDiaSemana] = useState<string>("0");
  const [motivo, setMotivo] = useState("");

  async function adicionar() {
    await criar.mutateAsync({
      profissionalId,
      tipo,
      dataInicio: tipo === "bloqueio_data" ? (dataInicio || null) : null,
      dataFim: tipo === "bloqueio_data" ? (dataFim || dataInicio || null) : null,
      diaSemana: tipo === "bloqueio_dia_semana" ? Number(diaSemana) : null,
      motivo: motivo || undefined,
    });
    setDataInicio("");
    setDataFim("");
    setMotivo("");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Restrições — {profissionalNome}</DialogTitle>
          <DialogDescription>
            Bloqueios de datas, dias da semana ou preferências.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-md border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-700">Nova restrição</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as typeof tipo)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bloqueio_data">Bloqueio de data</SelectItem>
                  <SelectItem value="bloqueio_dia_semana">Bloqueio dia da semana</SelectItem>
                  <SelectItem value="preferencia">Preferência</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {tipo === "bloqueio_data" ? (
              <>
                <div className="space-y-1.5">
                  <Label>Data início</Label>
                  <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Data fim (opcional)</Label>
                  <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
                </div>
              </>
            ) : tipo === "bloqueio_dia_semana" ? (
              <div className="space-y-1.5">
                <Label>Dia da semana</Label>
                <Select value={diaSemana} onValueChange={setDiaSemana}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIAS.map((d, i) => (
                      <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Motivo</Label>
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Opcional" />
            </div>
          </div>
          <Button onClick={adicionar} disabled={criar.isPending}>
            Adicionar restrição
          </Button>
        </div>

        <div className="mt-2">
          <div className="mb-2 text-sm font-semibold text-slate-700">Restrições</div>
          {(restrQ.data ?? []).length === 0 && (
            <div className="text-sm text-slate-500">Sem restrições cadastradas.</div>
          )}
          <ul className="space-y-2">
            {(restrQ.data ?? []).map((r) => (
              <li key={r.id} className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Badge variant={r.ativo ? "ativo" : "inativo"}>{r.ativo ? "Ativa" : "Inativa"}</Badge>
                    <span className="font-medium">
                      {r.tipo === "bloqueio_data" && `${r.dataInicio} → ${r.dataFim}`}
                      {r.tipo === "bloqueio_dia_semana" && `${DIAS[r.diaSemana ?? 0]}`}
                      {r.tipo === "preferencia" && "Preferência"}
                    </span>
                  </div>
                  {r.motivo && <div className="text-xs text-slate-500">{r.motivo}</div>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => toggle.mutate({ id: r.id })}>
                    {r.ativo ? "Desativar" : "Ativar"}
                  </Button>
                  <Button size="icon" variant="destructive" onClick={() => confirm("Excluir?") && excluir.mutate({ id: r.id })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
